const Debounce = require('lodash/debounce');
const {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} = require('react');
const FuzzyFilter = require('utils/fuzzyFilter');

exports.useClassMemberSearch = function useClassMemberSearch(props) {

    const {
        activeRoleGroupFilter,
        allClassmates,
        classId,
        initialSearchText,
        onSubmitSearch,
        roleGroupFilter,
        setClassSearchText,
        setRoleGroupFilter
    } = props;

    const [groupSearchVal, setGroupSearchVal] = useState(initialSearchText || '');
    const [hasUsedSearch, setHasUsedSearch] = useState(!!initialSearchText);
    const [submitting, setSubmitting] = useState(false);
    const requestIdRef = useRef(0);
    const mountedRef = useRef(true);

    useEffect(() => {

        mountedRef.current = true;

        return () => {

            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {

        setGroupSearchVal(initialSearchText || '');
        setHasUsedSearch(!!initialSearchText);
    }, [classId, initialSearchText]);

    const beginRequest = useCallback(() => {

        requestIdRef.current += 1;
        setSubmitting(true);

        return requestIdRef.current;
    }, []);

    const finishRequest = useCallback((requestId) => {

        if (mountedRef.current && requestId === requestIdRef.current) {
            setSubmitting(false);
        }
    }, []);

    const searchByName = useMemo(() => Debounce((name) => {

        const requestId = beginRequest();
        const criteria = name
            ? { classId, name }
            : { allByMatches: true, classId };

        return Promise.resolve(onSubmitSearch(criteria))
            .catch((err) => {

                console.error('[class detail] failed to search by name', {
                    classId,
                    name,
                    error: err
                });
            })
            .finally(() => finishRequest(requestId));
    }, 600), [beginRequest, classId, finishRequest, onSubmitSearch]);

    useEffect(() => () => searchByName.cancel(), [searchByName]);

    const onChangeSearch = useCallback((evt) => {

        const value = evt.target.value;

        searchByName(value);
        setClassSearchText({
            id: classId,
            text: value
        });
        setGroupSearchVal(value);
        setHasUsedSearch(true);
    }, [classId, searchByName, setClassSearchText]);

    const onChangeRoleGroupFilter = useCallback((nextRoleGroupFilter) => {

        if (nextRoleGroupFilter === roleGroupFilter) {
            return Promise.resolve();
        }

        const requestId = beginRequest();

        return Promise.resolve(setRoleGroupFilter(nextRoleGroupFilter, classId, groupSearchVal))
            .catch((err) => {

                console.error('[class detail] failed to search by role group', {
                    classId,
                    roleGroup: nextRoleGroupFilter,
                    error: err
                });
            })
            .finally(() => finishRequest(requestId));
    }, [beginRequest, classId, finishRequest, groupSearchVal, roleGroupFilter, setRoleGroupFilter]);

    const hasActiveRoleGroupFilter = !!activeRoleGroupFilter
        && activeRoleGroupFilter !== 'all'
        && activeRoleGroupFilter.name !== 'all';

    const filteredClassmates = useMemo(() => allClassmates
        .filter((user) => roleGroupFilter === 'all' || activeRoleGroupFilter?.id === user.role.roleGroupId)
        .filter((user) => FuzzyFilter(groupSearchVal, `${user.firstName} ${user.lastName}`)), [
        activeRoleGroupFilter,
        allClassmates,
        groupSearchVal,
        roleGroupFilter
    ]);

    return {
        classmates: groupSearchVal || hasActiveRoleGroupFilter ? filteredClassmates : allClassmates,
        groupSearchVal,
        hasActiveRoleGroupFilter,
        hasUsedSearch,
        onChangeRoleGroupFilter,
        onChangeSearch,
        submitting
    };
};
